import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { t, tn, tx } from '../../i18n';
import { fmtDate, fmtDateTime, fmtKB } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { Preview } from '../belege/BelegSheet';
import { belegePath } from '../belege/labels';
import { Loading } from '../belege/Pages';
import { Sheet } from '../belege/Sheet';
import { useClientApp } from '../context';
import { useCloseSheet } from '../lohn/Pages';
import { briefePath, letterCount, SENDERS, senderText, type Sender } from './labels';
import { useBriefePage } from './nav';
import type { LetterDetail, LetterItem, LettersResponse } from './types';
import { ShareFile } from '../../components/ShareFile';

// Ordner Briefe unter „Dokumente“: Briefe der Firma (in Bearbeitung und nach Absender) → Briefe eines Absenders.
// Brief (?brief=<id>) und „Neuen Brief hochladen“ (?neu=1) öffnen sich als Fenster.

const useLetters = (clientId: string) => useApi<LettersResponse>(`/api/app/briefe/${clientId}`, { live: ['briefe'], clientId });

// Fenster über der Seite öffnen (eigener Verlaufseintrag, so schließt auch die Zurück-Geste)
function useOpenSheet() {
  const navigate = useNavigate();
  return (search: string) => navigate({ search }, { state: { sheet: true } });
}

// Briefe · <Firma>: in Bearbeitung, Aufruf zum Hochladen, Ordner nach Absender
export function FirmPage({ clientId }: { clientId: string }) {
  const { clientName } = useClientApp();
  const res = useLetters(clientId);
  const open = useOpenSheet();
  const d = res.data;
  const name = d?.client.name ?? clientName(clientId);
  useBriefePage({ title: t('Briefe'), clientId, clientName: name });

  return (
    <>
      <div className="hello">
        <h2>{t('Briefe')}</h2>
        <p>
          {name} · {t('nach Absender sortiert')}
        </p>
      </div>
      {!d ? (
        <Loading error={res.error} />
      ) : (
        <>
          <InProgress letters={d.letters} onOpen={(id) => open(`?brief=${id}`)} />
          <button type="button" className="cta brief" onClick={() => open('?neu=1')}>
            <span>
              <b>✉ {t('Neuen Brief hochladen')}</b>
              <small>{t('Post von Ämtern, Krankenkasse, Bank & Co. · Foto oder PDF')}</small>
            </span>
            <span className="chev" aria-hidden="true">
              ›
            </span>
          </button>
          <div className="src bhint">
            {tn('Bitte hier keine Rechnungen oder Kontoauszüge hochladen. Alles für die Buchhaltung gehört unter {link}.', {
              link: (
                <Link className="linkbtn" to={belegePath(clientId)}>
                  {t('Finanzbuchhaltung')}
                </Link>
              ),
            })}
          </div>
          <div className="list">
            {SENDERS.map((s) => {
              const n = d.letters.filter((l) => l.sender === s && !l.senderPending).length;
              return (
                <Link key={s} className="item" to={briefePath(clientId, s)}>
                  <span className="ico" aria-hidden="true">
                    {t(s).charAt(0)}
                  </span>
                  <span className="tx">
                    <b>{t(s)}</b>
                    <small>{n ? letterCount(n) : t('noch keine')}</small>
                  </span>
                  {n > 0 && <span className="badge b-ok">{n}</span>}
                  <span className="chev" aria-hidden="true">
                    ›
                  </span>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

// „Briefe in Bearbeitung“: alle offenen, Rückfragen oben. Mit Rückfrage von selbst aufgeklappt.
function InProgress({ letters, onOpen }: { letters: LetterItem[]; onOpen(id: string): void }) {
  const items = letters.filter((l) => l.status === 'offen').sort((a, b) => Number(b.ask) - Number(a.ask));
  const asks = items.filter((l) => l.ask).length;
  const [opened, setOpened] = useState<boolean | null>(null);
  if (!items.length) return null;
  return (
    <details className="inb" open={opened ?? asks > 0} onToggle={(e) => setOpened(e.currentTarget.open)}>
      <summary>
        <b>{t('Briefe in Bearbeitung')}</b>
        <span className="badge b-none">{items.length}</span>
        {asks > 0 && <span className="badge b-warn">{asks === 1 ? t('1 Rückfrage') : t('{n} Rückfragen', { n: asks })}</span>}
      </summary>
      <div className="list">
        {items.map((l) => (
          <button key={l.id} type="button" className="item" onClick={() => onOpen(l.id)}>
            <span className="ico" aria-hidden="true">
              ✉
            </span>
            <span className="tx">
              <b>{t(senderText(l))}</b>
              <small>
                {l.name} · {uploaded(l)}
              </small>
              {/* unter dem Text, sonst drückt der lange Hinweis am Handy den Namen zusammen */}
              {l.ask ? (
                <span className="badge b-warn">{t('Rückfrage an Sie – bitte antworten')}</span>
              ) : (
                <span className="badge b-none">{t('Kanzlei prüft')}</span>
              )}
            </span>
            <span className="chev" aria-hidden="true">
              ›
            </span>
          </button>
        ))}
      </div>
    </details>
  );
}

// <Absender> · Briefe dieses Absenders, neueste zuerst
export function FolderPage({ clientId, sender }: { clientId: string; sender: Sender }) {
  const { clientName } = useClientApp();
  const res = useLetters(clientId);
  const open = useOpenSheet();
  const d = res.data;
  const name = d?.client.name ?? clientName(clientId);
  useBriefePage({ title: t(sender), clientId, clientName: name, sub: t(sender) });
  const letters = d?.letters.filter((l) => l.sender === sender && !l.senderPending) ?? [];

  return (
    <>
      <div className="hello">
        <h2>{t(sender)}</h2>
        <p>
          {t('Briefe')} · {name}
        </p>
      </div>
      {!d ? (
        <Loading error={res.error} />
      ) : (
        <div className="files">
          <h3>
            {t('Briefe')} ({letters.length})
          </h3>
          {letters.map((l) => (
            <button key={l.id} type="button" className="file" onClick={() => open(`?brief=${l.id}`)}>
              <span>
                {l.name}
                <small className="fsub">{uploaded(l)}</small>
              </span>
              {l.status === 'erledigt' ? (
                <span className="ok">{t('erledigt')} ✓</span>
              ) : l.ask ? (
                <span className="wait">{t('Rückfrage an Sie')}</span>
              ) : (
                <span className="wait idle">{t('Kanzlei prüft')}</span>
              )}
            </button>
          ))}
          {!letters.length && <div className="empty">{t('Noch keine Briefe.')}</div>}
        </div>
      )}
    </>
  );
}

export function LetterSheetHost() {
  const [params] = useSearchParams();
  const close = useCloseSheet('brief');
  const id = params.get('brief');
  return id ? <LetterSheet key={id} id={id} onClose={close} /> : null;
}

// Briefe aus dem Posteingang der Kanzlei: eingescannt statt hochgeladen
const uploaded = (l: { fromOffice: boolean; createdAt: string }) =>
  l.fromOffice
    ? t('von Ihrer Kanzlei eingescannt am {date}', { date: fmtDate(l.createdAt) })
    : t('hochgeladen am {date}', { date: fmtDate(l.createdAt) });

const askOf = (l: LetterDetail) => l.status === 'offen' && l.comments.at(-1)?.side === 'staff';

function statusText(l: LetterDetail): string {
  if (l.status === 'erledigt') {
    return l.doneAt ? t('Erledigt von Ihrer Kanzlei am {date}', { date: fmtDate(l.doneAt) }) : t('Erledigt von Ihrer Kanzlei');
  }
  if (askOf(l)) return t('Ihre Kanzlei hat eine Rückfrage. Bitte antworten Sie unten.');
  return t('Ihre Kanzlei prüft den Brief und meldet sich, falls etwas zu tun ist.');
}

// Brief ansehen und kommentieren
function LetterSheet({ id, onClose }: { id: string; onClose(): void }) {
  const res = useApi<LetterDetail>(`/api/app/brief/${id}`, { live: ['briefe'] });
  const l = res.data;

  // Meldungen der Kanzlei zu diesem Brief (Kommentar, erledigt, eingescannt) gelten als gelesen, sobald das Fenster
  // sie zeigt
  const staffComments = l?.comments.filter((c) => c.side === 'staff').length ?? 0;
  const done = l?.status === 'erledigt';
  const office = !!l?.fromOffice;
  useEffect(() => {
    if (staffComments > 0 || done || office) api.post(`/api/app/brief/${id}/read`).catch(() => {});
  }, [id, staffComments, done, office]);

  if (!l) {
    return (
      <Sheet title={t('Brief')} onClose={onClose}>
        {res.error ? <p className="meta">{res.error}</p> : <div className="spinner" aria-label={t('Lädt')} />}
      </Sheet>
    );
  }

  const fileUrl = `/api/app/brief/${l.id}/file`;
  return (
    <Sheet title={l.name} onClose={onClose}>
      <Preview url={fileUrl} mime={l.mime} name={l.name} />
      <div className="meta">
        {t(senderText(l))} ·{' '}
        {l.fromOffice
          ? uploaded(l)
          : l.createdBy
            ? t('hochgeladen am {date} von {name}', { date: fmtDate(l.createdAt), name: l.createdBy })
            : uploaded(l)}{' '}
        · {fmtKB(l.size)}
      </div>
      <p className={`bstat${l.status === 'erledigt' ? ' done' : askOf(l) ? ' ask' : ''}`} role="status">
        {statusText(l)}
      </p>
      <div className="row">
        <a className="btn2" href={`${fileUrl}?download=1`} download>
          {t('Herunterladen')}
        </a>
        <ShareFile url={`${fileUrl}?download=1`} name={l.name} />
      </div>
      <Comments l={l} reload={res.reload} />
    </Sheet>
  );
}

function Comments({ l, reload }: { l: LetterDetail; reload(): Promise<void> }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post(`/api/app/brief/${l.id}/comments`, { body });
      setText('');
      await reload();
      toast(t('Kommentar an Ihre Kanzlei gesendet'));
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rthread">
      <div className="rt-h">{t('Kommentare zum Brief')}</div>
      {l.comments.map((c) => (
        <div key={c.id} className={`rc${c.mine ? ' rc-me' : ''}`}>
          <b>{c.mine ? t('Sie') : tx(c.authorName)}</b> <span>{fmtDateTime(c.createdAt)}</span>
          <div>{c.body}</div>
        </div>
      ))}
      <form className="rc-form" onSubmit={submit}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={1000}
          placeholder={t('Kommentar an Ihre Kanzlei')}
          aria-label={t('Kommentar zu {name}', { name: l.name })}
        />
        <button type="submit" className="btn2" disabled={busy}>
          {t('Senden')}
        </button>
      </form>
    </div>
  );
}
